import { useQueries } from "@tanstack/react-query";
import { Document } from "@models";
import { Template } from "@types";
import { useMemo } from "react";
import { sortDevicesByName } from "src/utils/sorting";
import {
  getDocumentsParams,
  getDocumentsTemplatesParams,
  useGetDevices,
} from "./queries";
import { getPreferredDeviceId } from "./useDocumentRouteSelection";

type UseAllDeviceDocumentsIndexArgs = {
  enabled: boolean;
  orgId: string;
  preferredDeviceId?: string | null;
  queryDeviceId: string | null;
  routeDeviceId: string | null;
};

const mergeDocumentsById = (documents: Document[][]) => {
  const mergedDocumentsById = new Map<string, Document>();

  documents.flat().forEach((document) => {
    if (!mergedDocumentsById.has(document.id)) {
      mergedDocumentsById.set(document.id, document);
    }
  });

  return [...mergedDocumentsById.values()];
};

export const useAllDeviceDocumentsIndex = ({
  enabled,
  orgId,
  preferredDeviceId,
  queryDeviceId,
  routeDeviceId,
}: UseAllDeviceDocumentsIndexArgs) => {
  const devicesQuery = useGetDevices({
    orgId,
    enabled,
  });
  const devices = useMemo(
    () => sortDevicesByName(devicesQuery.data ?? []),
    [devicesQuery.data]
  );

  const templatesQueries = useQueries({
    queries: devices.map((device) =>
      getDocumentsTemplatesParams({
        orgId,
        deviceId: device.id,
        enabled,
        explorer: true,
      })
    ),
  });

  const templatesConfigByDeviceId = useMemo(() => {
    const nextTemplatesConfigByDeviceId = new Map<string, Record<string, Template>>();

    devices.forEach((device, index) => {
      const templatesConfig = templatesQueries[index]?.data;

      if (templatesConfig) {
        nextTemplatesConfigByDeviceId.set(device.id, templatesConfig);
      }
    });

    return nextTemplatesConfigByDeviceId;
  }, [devices, templatesQueries]);

  const documentsQueries = useQueries({
    queries: devices.map((device) =>
      getDocumentsParams(orgId, device.id, {
        enabled,
        explorer: true,
        includeOrgDocs: true,
      })
    ),
  });

  const usableDevices = useMemo(
    () =>
      devices.filter((device, index) => {
        const templateQuery = templatesQueries[index];
        const documentsQuery = documentsQueries[index];

        return (
          templatesConfigByDeviceId.has(device.id) &&
          templateQuery?.isError !== true &&
          documentsQuery?.isError !== true
        );
      }),
    [devices, documentsQueries, templatesConfigByDeviceId, templatesQueries]
  );
  const hasPerDeviceQueryError =
    templatesQueries.some((query) => query.isError) ||
    documentsQueries.some((query) => query.isError);
  const devicesForContext = usableDevices.length > 0 ? usableDevices : devices;
  const currentDeviceId = useMemo(
    () =>
      getPreferredDeviceId({
        devices,
        fallbackDevices: devicesForContext,
        preferredDeviceId,
        queryDeviceId,
        routeDeviceId,
      }),
    [devices, devicesForContext, preferredDeviceId, queryDeviceId, routeDeviceId]
  );

  const documents = useMemo(
    () =>
      mergeDocumentsById(
        documentsQueries.map((query) => (query.data as Document[] | undefined) ?? [])
      ),
    [documentsQueries]
  );

  const findTemplateByKey = (templateKey: string | null | undefined) => {
    if (!templateKey) {
      return undefined;
    }

    for (const device of devicesForContext) {
      const template = templatesConfigByDeviceId.get(device.id)?.[templateKey];

      if (template) {
        return template;
      }
    }

    return undefined;
  };

  const resolveTemplateForDocument = (document: Document) => {
    if (!document.templateKey) {
      return undefined;
    }

    if (document.deviceId) {
      return (
        templatesConfigByDeviceId.get(document.deviceId)?.[document.templateKey] ??
        (currentDeviceId
          ? templatesConfigByDeviceId.get(currentDeviceId)?.[document.templateKey]
          : undefined) ??
        findTemplateByKey(document.templateKey)
      );
    }

    return (
      (currentDeviceId
        ? templatesConfigByDeviceId.get(currentDeviceId)?.[document.templateKey]
        : undefined) ?? findTemplateByKey(document.templateKey)
    );
  };

  const getDocumentNavigationDeviceId = (document: Document) =>
    document.deviceId ?? currentDeviceId ?? null;

  return {
    currentDeviceId,
    currentTemplatesConfig: currentDeviceId
      ? templatesConfigByDeviceId.get(currentDeviceId)
      : undefined,
    devices: devicesForContext,
    documents,
    documentQueryDeviceIds: devicesForContext.map((device) => device.id),
    getDocumentNavigationDeviceId,
    getTemplatesConfigForDevice: (deviceId: string) =>
      templatesConfigByDeviceId.get(deviceId),
    isError:
      devicesQuery.isError || (devices.length > 0 && hasPerDeviceQueryError && usableDevices.length === 0),
    isLoading:
      devicesQuery.isLoading ||
      templatesQueries.some((query) => query.isLoading) ||
      documentsQueries.some((query) => query.isLoading),
    resolveTemplateForDocument,
  };
};
